import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Command, Loader2, RefreshCw, AlertTriangle, ShieldCheck,
  Activity, ListChecks, Sunrise,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import ExecutiveIntelligence from "./ExecutiveIntelligence";
import IdentityHealthCard from "../../components/IdentityHealthCard";
import "./ExecutiveOperations.css";

const VIEW_LABELS = {
  executive_director: "Executive Director",
  board: "Board",
  operations: "Operations",
  care_supervisor: "Care Supervisor",
  grant_manager: "Grant Manager",
  security_compliance: "Security & Compliance",
};

export default function ExecutiveOperations() {
  const [availableViews, setAvailableViews] = useState([]);
  const [currentView, setCurrentView] = useState(null);
  const [viewPayload, setViewPayload] = useState(null);
  const [header, setHeader] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [timelineFilters, setTimelineFilters] = useState({
    severity: "", county: "", program: "", since_days: 30,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [digestCard, setDigestCard] = useState(null);
  const [runningDigest, setRunningDigest] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const [views, h, t, digest] = await Promise.all([
        api.get("/executive-ops/command-views"),
        api.get("/executive-ops/summary"),
        api.get(`/executive-ops/timeline?since_days=${timelineFilters.since_days}`),
        api.get("/executive-ops/daily-digest/latest").catch(() => ({ data: null })),
      ]);
      const accessible = (views.data.views || []).filter((v) => v.accessible);
      setAvailableViews(views.data.views || []);
      setHeader(h.data);
      setTimeline(t.data.events || []);
      setDigestCard(digest.data && digest.data.id ? digest.data : null);
      // Pick first accessible view by default
      if (!currentView && accessible[0]) setCurrentView(accessible[0].key);
    } catch (e) { setError(formatApiError(e)); }
    finally { setLoading(false); }
  }, [timelineFilters.since_days, currentView]);

  useEffect(() => { load(); }, [load]);

  const loadView = useCallback(async (key) => {
    if (!key) return;
    setLoading(true); setError("");
    try {
      const { data } = await api.get(`/executive-ops/command-view?view=${key}`);
      setViewPayload(data);
    } catch (e) { setError(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (currentView) loadView(currentView);
  }, [currentView, loadView]);

  const refreshTimeline = useCallback(async () => {
    const qs = new URLSearchParams();
    Object.entries(timelineFilters).forEach(([k, v]) => {
      if (v) qs.set(k, v);
    });
    try {
      const { data } = await api.get(`/executive-ops/timeline?${qs.toString()}`);
      setTimeline(data.events || []);
    } catch (e) { setError(formatApiError(e)); }
  }, [timelineFilters]);

  useEffect(() => { refreshTimeline(); }, [refreshTimeline]);

  const strip = useMemo(() => {
    if (!header) return [];
    return [
      { label: "Org Health", value: header.organization_health?.score,
        meta: header.organization_health?.grade,
        testid: "eoc-strip-orghealth" },
      { label: "Active alerts",
        value: header.active_alerts?.open,
        meta: `${header.active_alerts?.critical || 0} critical`,
        tone: header.active_alerts?.critical > 0 ? "crit" : "",
        testid: "eoc-strip-alerts" },
      { label: "Programs at risk", value: header.programs_at_risk,
        tone: header.programs_at_risk > 0 ? "warn" : "ok",
        testid: "eoc-strip-programs-risk" },
      { label: "Counties requiring attention",
        value: header.counties_requiring_attention,
        tone: header.counties_requiring_attention > 0 ? "warn" : "ok",
        testid: "eoc-strip-counties" },
      { label: "Partners active",
        value: `${header.partners?.active}/${header.partners?.total}`,
        testid: "eoc-strip-partners" },
      { label: "Volunteer shortages",
        value: header.volunteer_shortages,
        tone: header.volunteer_shortages > 0 ? "warn" : "ok",
        testid: "eoc-strip-vols" },
      { label: "Security", value: `${header.security_status?.score ?? "—"}%`,
        meta: (header.security_status?.last_run_at || "").slice(0, 10) || "no scan",
        testid: "eoc-strip-security" },
      { label: "Compliance",
        value: header.compliance_status,
        tone: header.compliance_status === "aligned" ? "ok" : "warn",
        testid: "eoc-strip-compliance" },
    ];
  }, [header]);

  return (
    <div className="hw-eoc" data-testid="executive-operations">
      <div className="hw-eoc__hero">
        <div className="hw-eoc__eyebrow">
          <Command className="w-3.5 h-3.5"
                    style={{ display: "inline", verticalAlign: "text-bottom",
                              marginRight: "0.4rem" }} />
          Enterprise Command Center
        </div>
        <h1 className="hw-eoc__title">Executive Operations Center</h1>
        <p className="hw-eoc__sub">
          Live cross-module operational awareness. Every widget below is composed
          from real HarborWell data — no duplication, no lag.
        </p>
        <div className="hw-eoc__strip" data-testid="eoc-summary-strip">
          {strip.map((s) => (
            <div key={s.label} className="hw-eoc__strip-tile" data-testid={s.testid}>
              <div className="hw-eoc__strip-label">{s.label}</div>
              <div className={`hw-eoc__strip-value ${s.tone ? `hw-eoc__strip-${s.tone}` : ""}`}>
                {s.value ?? "—"}
              </div>
              {s.meta && <div className="hw-eoc__strip-meta">{s.meta}</div>}
            </div>
          ))}
        </div>
      </div>

      {digestCard && (
        <div className="hw-eoc__widget" data-testid="morning-digest-card"
              style={{ marginTop: "1rem",
                       background: "linear-gradient(135deg, #B45309 0%, #7C2D12 100%)",
                       color: "#FFF7ED",
                       border: "1px solid #F59E0B" }}>
          <h3 style={{ color: "rgba(255,255,255,0.85)" }}>
            <Sunrise className="w-3.5 h-3.5"
                       style={{ display: "inline", verticalAlign: "text-bottom",
                                 marginRight: "0.35rem" }} />
            Morning Executive Digest ·{" "}
            {(digestCard.posted_at || "").slice(0, 10)}
            {digestCard.copilot_used ? (
              <span style={{ marginLeft: "0.5rem", fontSize: "0.65rem",
                              background: "rgba(255,255,255,0.18)",
                              padding: "0.15rem 0.5rem", borderRadius: 10 }}>
                Copilot
              </span>
            ) : (
              <span style={{ marginLeft: "0.5rem", fontSize: "0.65rem",
                              background: "rgba(255,255,255,0.18)",
                              padding: "0.15rem 0.5rem", borderRadius: 10 }}>
                Fallback
              </span>
            )}
          </h3>
          <div style={{ fontSize: "0.9rem", whiteSpace: "pre-wrap",
                          lineHeight: 1.55 }}
                data-testid="morning-digest-narrative">
            {digestCard.narrative}
          </div>
          {(digestCard.citations || []).length > 0 && (
            <div style={{ fontSize: "0.72rem", marginTop: "0.7rem",
                          color: "rgba(255,255,255,0.75)" }}
                  data-testid="morning-digest-citations">
              <strong>Sources:</strong>{" "}
              {digestCard.citations.join(" · ")}
            </div>
          )}
        </div>
      )}

      {!digestCard && (
        <div className="hw-eoc__widget" style={{ marginTop: "0.8rem" }}
              data-testid="morning-digest-empty">
          <h3>Morning Executive Digest</h3>
          <p style={{ color: "#94A3B8", fontSize: "0.85rem", margin: "0 0 0.5rem" }}>
            No digest posted yet today. Runs automatically at 06:00 ET.
          </p>
          <button className="hw-eoc__nav-btn hw-eoc__nav-btn--active"
                  disabled={runningDigest}
                  onClick={async () => {
                    setRunningDigest(true);
                    try {
                      await api.post("/executive-ops/daily-digest/run-now");
                      await load();
                    } catch (e) { setError(formatApiError(e)); }
                    finally { setRunningDigest(false); }
                  }}
                  data-testid="digest-run-now">
            {runningDigest ? (
              <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Generating…</>
            ) : "Generate morning digest now"}
          </button>
        </div>
      )}

      {/* Identity Health widget — Iter 74.5 */}
      <div style={{ marginBottom: "1rem" }} data-testid="eoc-identity-health-slot">
        <IdentityHealthCard />
      </div>


      <div className="hw-eoc__nav" data-testid="eoc-view-nav">
        {availableViews.map((v) => (
          <button key={v.key}
                  className={`hw-eoc__nav-btn ${currentView === v.key ? "hw-eoc__nav-btn--active" : ""}`}
                  disabled={!v.accessible}
                  onClick={() => setCurrentView(v.key)}
                  data-testid={`eoc-nav-${v.key}`}>
            {VIEW_LABELS[v.key] || v.key}
            {!v.accessible && " (locked)"}
          </button>
        ))}
        <button className="hw-eoc__nav-btn" onClick={load}
                data-testid="eoc-refresh"
                style={{ marginLeft: "auto" }}>
          <RefreshCw className="w-3.5 h-3.5"
                      style={{ display: "inline", verticalAlign: "text-bottom",
                                marginRight: "0.3rem" }} /> Refresh
        </button>
      </div>

      {error && <div style={{ color: "#FCA5A5" }} data-testid="eoc-error">{error}</div>}
      {loading && (
        <div style={{ color: "#94A3B8" }}>
          <Loader2 className="w-4 h-4 animate-spin"
                    style={{ display: "inline", verticalAlign: "text-bottom" }} /> loading…
        </div>
      )}

      <div className="hw-eoc__grid">
        <div>
          {viewPayload?.widgets ? (
            viewPayload.widgets.map((w) => (
              <Widget key={w.id} widget={w} />
            ))
          ) : (
            !loading && <div className="hw-eoc__empty">Select a view above.</div>
          )}
        </div>

        <div className="hw-eoc__widget" data-testid="eoc-timeline-widget">
          <h3>
            <Activity className="w-3.5 h-3.5"
                        style={{ display: "inline", verticalAlign: "text-bottom",
                                  marginRight: "0.3rem" }} />
            Executive Timeline
          </h3>
          <div className="hw-eoc__filter-row">
            <select value={timelineFilters.severity}
                    onChange={(e) => setTimelineFilters({
                      ...timelineFilters, severity: e.target.value,
                    })}
                    data-testid="eoc-timeline-severity">
              <option value="">All severities</option>
              <option value="critical">Critical</option>
              <option value="warning">Warning</option>
              <option value="info">Info</option>
            </select>
            <select value={timelineFilters.since_days}
                    onChange={(e) => setTimelineFilters({
                      ...timelineFilters, since_days: Number(e.target.value),
                    })}
                    data-testid="eoc-timeline-window">
              <option value="7">7 days</option>
              <option value="30">30 days</option>
              <option value="90">90 days</option>
            </select>
            <input placeholder="County"
                    value={timelineFilters.county}
                    onChange={(e) => setTimelineFilters({
                      ...timelineFilters, county: e.target.value,
                    })}
                    data-testid="eoc-timeline-county" />
            <input placeholder="Program"
                    value={timelineFilters.program}
                    onChange={(e) => setTimelineFilters({
                      ...timelineFilters, program: e.target.value,
                    })}
                    data-testid="eoc-timeline-program" />
          </div>
          <div className="hw-eoc__timeline" data-testid="eoc-timeline-list">
            {timeline.length === 0 && (
              <div className="hw-eoc__empty">No events in this window.</div>
            )}
            {timeline.map((e) => (
              <div key={e.id}
                    className={`hw-eoc__timeline-item hw-eoc__timeline-item--${e.severity || "info"}`}
                    data-testid={`eoc-timeline-${e.id}`}>
                <div className="hw-eoc__timeline-meta">
                  <span>{e.source.replace(/_/g, " ")}</span>
                  <span>·</span>
                  <span>{(e.at || "").slice(0, 16).replace("T", " ")}</span>
                </div>
                <div className="hw-eoc__timeline-title">{e.title}</div>
                {e.body && (
                  <div className="hw-eoc__timeline-body">{e.body.slice(0, 220)}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <ExecutiveIntelligence />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Widget renderer — generic dispatcher based on widget.kind          */
/* ------------------------------------------------------------------ */
function Widget({ widget }) {
  const { id, kind, title } = widget;
  if (kind === "score") {
    return (
      <div className="hw-eoc__widget hw-eoc__widget--score" data-testid={`widget-${id}`}>
        <h3>{title}</h3>
        <div className="hw-eoc__widget-score-value" data-testid={`${id}-score`}>
          {widget.score ?? "—"}
        </div>
        {widget.grade && (
          <div className="hw-eoc__widget-grade" data-testid={`${id}-grade`}>
            Grade {widget.grade}
          </div>
        )}
        {widget.top_negatives && widget.top_negatives.length > 0 && (
          <div className="hw-eoc__widget-contribs">
            <strong>Watch:</strong>{" "}
            {widget.top_negatives.map((n) => n.label).join(", ")}
          </div>
        )}
      </div>
    );
  }

  if (kind === "kpi_group" || kind === "kpi") {
    const kpis = kind === "kpi_group" ? widget.kpis || {} : { [title]: widget.value };
    return (
      <div className="hw-eoc__widget" data-testid={`widget-${id}`}>
        <h3>{title}</h3>
        <div className="hw-eoc__kpi-grid">
          {Object.entries(kpis).map(([k, v]) => (
            <div key={k} className="hw-eoc__kpi-tile">
              <div className="hw-eoc__kpi-label">{k.replace(/_/g, " ")}</div>
              <div className="hw-eoc__kpi-value">
                {typeof v === "object" ? JSON.stringify(v) : String(v ?? "—")}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (kind === "table") {
    const cols = widget.columns || [];
    const rows = widget.rows || [];
    return (
      <div className="hw-eoc__widget" data-testid={`widget-${id}`}>
        <h3>{title}</h3>
        <div style={{ overflowX: "auto" }}>
          <table className="hw-eoc__table">
            <thead>
              <tr>{cols.map((c) => <th key={c}>{c.replace(/_/g, " ")}</th>)}</tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  {cols.map((c) => (
                    <td key={c}>
                      {typeof r[c] === "object" ? JSON.stringify(r[c]) : String(r[c] ?? "—")}
                    </td>
                  ))}
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={cols.length}
                          style={{ textAlign: "center", color: "#64748B", padding: "0.7rem" }}>
                  No data.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  if (kind === "list") {
    return (
      <div className="hw-eoc__widget" data-testid={`widget-${id}`}>
        <h3>
          <ListChecks className="w-3.5 h-3.5"
                        style={{ display: "inline", verticalAlign: "text-bottom",
                                  marginRight: "0.3rem" }} />
          {title}
        </h3>
        <ul className="hw-eoc__list">
          {(widget.items || []).map((it, i) => (
            <li key={i} className="hw-eoc__list-item">
              <span>{it.title || it.key || JSON.stringify(it)}</span>
              <small>{it.severity || it.at || it.score || ""}</small>
            </li>
          ))}
          {(widget.items || []).length === 0 && (
            <li className="hw-eoc__list-item">
              <small>No items.</small>
            </li>
          )}
        </ul>
      </div>
    );
  }

  return (
    <div className="hw-eoc__widget" data-testid={`widget-${id}`}>
      <h3>{title}</h3>
      <pre style={{ fontSize: "0.7rem", color: "#94A3B8" }}>{JSON.stringify(widget, null, 2)}</pre>
    </div>
  );
}
